import { Button, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ThemeContext } from "../utils/themes/ThemeContext";
import { IoMdClose, IoMdTrophy } from 'react-icons/io';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const S3_BASE_URL = "https://documents.junior-entreprises.com/kiwi-public/";

const GirlChoice = ({ choice, setChoice, profile }) => {
  const { theme } = useContext(ThemeContext);

  return (
    profile && (
      <Box
        onClick={() => setChoice(profile.id)}
        component="img"
        src={`${S3_BASE_URL}/${profile.picture_url}`}
        alt={`Picture of girl number ${profile.id}`}
        sx={{
          borderRadius: { xs: "16px", sm: "24px", md: "30px" },
          objectFit: "cover",
          width: { xs: "60vw", sm: "32vw", md: "30vw" },
          height: { xs: "60vw", sm: "32vw", md: "30vw" },
          maxWidth: "400px",
          maxHeight: "400px",
          borderWidth: { xs: 3, sm: 4, md: 5 },
          borderStyle: "solid",
          borderColor: profile.id === choice ? theme.PrimaryColor : theme.SecondaryColor,
          cursor: "pointer",
          transition: "border-color 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease",
          transform: profile.id === choice ? "scale(1.03)" : "scale(1)",
          boxShadow: profile.id === choice
            ? `0 8px 32px ${theme.PrimaryColor}55`
            : "0 4px 16px rgba(0,0,0,0.15)",
          ":hover": {
            borderColor: choice !== profile.id ? theme.GreyColor : undefined,
            transform: "scale(1.02)",
          },
        }}
      />
    )
  );
};

const ScoreboardPanel = ({ open, onClose, theme }) => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const { organizationId } = useParams();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_URL}/api/profiles/`,
        { params: { organization_id: organizationId, sex: "girl", limit: 10 } }
      )
      .then((res) => setScores(res.data || []))
      .catch(() => setScores([]))
      .finally(() => setLoading(false));
  }, [open, organizationId]);

  const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

  return (
    <>
      <Box
        onClick={onClose}
        sx={{
          display: { xs: open ? "block" : "none", md: "none" },
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1200,
          backdropFilter: "blur(2px)",
        }}
      />

      <Box
        sx={{
          position: "fixed",
          zIndex: 1300,

          top: { md: 0 },
          right: { md: 0 },

          bottom: { xs: 0, md: "unset" },
          left: { xs: 0, md: "unset" },
          width: { xs: "100%", md: "320px" },
          height: { xs: "70vh", md: "100vh" },
          borderRadius: { xs: "20px 20px 0 0", md: 0 },
          transform: {
            xs: open ? "translateY(0)" : "translateY(100%)",
            md: open ? "translateX(0)" : "translateX(100%)",
          },

          backgroundColor: theme.NeutralColor,
          borderLeft: { md: `2px solid ${theme.PrimaryColor}33` },
          borderTop: { xs: `2px solid ${theme.PrimaryColor}33`, md: "none" },
          boxShadow: open ? "-8px 0 40px rgba(0,0,0,0.25)" : "none",
          transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${theme.PrimaryColor}22`,
            flexShrink: 0,
          }}
        >
            <IoMdTrophy size={24} color={theme.PrimaryColor} />
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: 20,
              color: theme.AccentColor,
              letterSpacing: "-0.3px",
            }}
          >
            Scoreboard
          </Typography>
          <Box
            onClick={onClose}
            sx={{
              cursor: "pointer",
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: `${theme.GreyColor}22`,
              color: theme.AccentColor,
              fontSize: 18,
              fontWeight: "bold",
              transition: "background-color 0.2s",
              ":hover": { backgroundColor: `${theme.GreyColor}44` },
            }}
          >
            <IoMdClose size={18} />
          </Box>
        </Box>

        <Box sx={{ overflowY: "auto", flex: 1, padding: "12px 0" }}>
          {loading ? (
            <Typography
              sx={{
                fontFamily: "Poppins",
                textAlign: "center",
                color: theme.GreyColor,
                mt: 4,
                fontSize: 14,
              }}
            >
              Loading…
            </Typography>
          ) : scores.length === 0 ? (
            <Typography
              sx={{
                fontFamily: "Poppins",
                textAlign: "center",
                color: theme.GreyColor,
                mt: 4,
                fontSize: 14,
              }}
            >
              No scores yet.
            </Typography>
          ) : (
            scores.map((profile, index) => (
              <Box
                key={profile.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  padding: "10px 24px",
                  transition: "background-color 0.15s",
                  ":hover": { backgroundColor: `${theme.PrimaryColor}11` },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 700,
                    fontSize: 15,
                    minWidth: 28,
                    color: index < 3 ? medalColors[index] : theme.GreyColor,
                  }}
                >
                  {index < 3 ? ["🥇", "🥈", "🥉"][index] : `#${index + 1}`}
                </Typography>

                <Box
                  component="img"
                  src={`${S3_BASE_URL}/${profile.picture_url}`}
                  alt={profile.name}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: `2px solid ${index < 3 ? medalColors[index] : theme.GreyColor + "55"}`,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 14,
                    fontWeight: 600,
                    color: theme.AccentColor,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {profile.name}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 13,
                    fontWeight: 700,
                    color: theme.PrimaryColor,
                    flexShrink: 0,
                  }}
                >
                  {Math.round(profile.elo_score ?? 1000)}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </>
  );
};

const VotePage = () => {
  const { theme } = useContext(ThemeContext);
  const { organizationId } = useParams();
  const [choice, setChoice] = useState(null);
  const [girls, setGirls] = useState([]);
  const [error, setError] = useState(false);
  const [scoreboardOpen, setScoreboardOpen] = useState(false);

  const fetchGirls = useCallback(async () => {
    try {
        const params = { organization_id: organizationId, sex: "girl" };
        const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/profiles/random`,
        { params }
        );
        setGirls(response.data);
    } catch (error) {
        console.error(error);
    }
    }, [organizationId]);

    useEffect(() => {
    fetchGirls();
    }, [fetchGirls]);

  const chooseGirl = async () => {
    if (choice) {
      const body = {
        winnerId: choice,
        loserId: girls.find((girl) => girl.id !== choice).id,
      };
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/profiles/update-elo`,
        body
      );
      fetchGirls();
      setChoice(null);
      setError(false);
    } else {
      setError(true);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.NeutralColor,
        minHeight: "100vh",
        color: theme.AccentColor,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: "120px",
        transition: "padding-right 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        paddingRight: {
          md: scoreboardOpen ? "340px" : "24px",
        },
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Poppins",
          fontSize: { xs: 20, sm: 24, md: 30 },
          textAlign: "center",
          marginBottom: { xs: "28px", sm: "40px", md: "50px" },
          fontWeight: 600,
          lineHeight: 1.3,
          maxWidth: "480px",
          px: 1,
        }}
      >
        Who is the hottest?
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          maxWidth: "900px",
          gap: { xs: "12px", sm: 0 },
        }}
      >
        <GirlChoice choice={choice} setChoice={setChoice} profile={girls[0]} />

        <Typography
          sx={{
            fontFamily: "Poppins",
            fontWeight: 800,
            fontSize: { xs: "5vw", sm: "6vw", md: "3.5vw", lg: "2.5rem" },
            margin: { xs: "4px 0", sm: "0 4vw", md: "0 5vw" },
            letterSpacing: "0.05em",
            color: theme.AccentColor,
            opacity: 0.75,
            flexShrink: 0,
          }}
        >
          OR
        </Typography>

        <GirlChoice choice={choice} setChoice={setChoice} profile={girls[1]} />
      </Box>

      <Button
        onClick={chooseGirl}
        sx={{
          marginTop: { xs: "28px", sm: "40px", md: "50px" },
          backgroundColor: theme.PrimaryColor,
          color: theme.NeutralColor,
          borderRadius: 2,
          padding: { xs: "8px 32px", sm: "10px 50px" },
          fontWeight: "bold",
          fontSize: { xs: 16, sm: 18, md: 20 },
          fontFamily: "Poppins",
          textTransform: "none",
          ":hover": { backgroundColor: theme.SecondaryColor },
        }}
      >
        Choose
      </Button>

      <Typography
        sx={{
          marginTop: "16px",
          fontFamily: "Poppins",
          fontSize: { xs: 13, sm: 14 },
          visibility: error ? "visible" : "hidden",
          color: theme.PrimaryColor,
          textAlign: "center",
        }}
      >
        You must choose a girl. No skip!
      </Typography>

      <Box
        onClick={() => setScoreboardOpen((v) => !v)}
        title="Scoreboard"
        sx={{
          position: "fixed",
          right: { xs: 16, md: scoreboardOpen ? "332px" : 16 },
          bottom: { xs: 16, md: "unset" },
          top: { xs: "unset", md: "50%" },
          transform: { xs: "none", md: "translateY(-50%)" },

          width: { xs: 52, md: 44 },
          height: { xs: 52, md: 44 },
          borderRadius: "50%",
          backgroundColor: theme.PrimaryColor,
          color: theme.NeutralColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: { xs: 22, md: 20 },
          cursor: "pointer",
          zIndex: 1100,
          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
          transition: "background-color 0.2s, right 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          ":hover": { backgroundColor: theme.SecondaryColor },
        }}
      >
        <IoMdTrophy size={20} />
      </Box>

      <ScoreboardPanel
        open={scoreboardOpen}
        onClose={() => setScoreboardOpen(false)}
        theme={theme}
      />
    </Box>
  );
};

export default VotePage;
